import { useEffect, useRef } from 'react'

import { AuthStatus } from '../machine/types'
import { useAuth } from './useAuth'

export interface UseRequireAuthOptions {
    /** Fires when the runtime resolves to `unauthenticated`. */
    onUnauthenticated?: () => void
    /** Fires when refresh fails terminally and the user must re-auth. */
    onReauthRequired?: () => void
    /**
     * Required roles (any-of). When set and the user lacks all of them,
     * `onForbidden` fires.
     */
    requireRoles?: readonly string[]
    /** Fires when authenticated but missing required roles. */
    onForbidden?: () => void
}

export interface UseRequireAuthValue {
    status: AuthStatus
    isAuthenticated: boolean
    /** True when authenticated AND has any of `requireRoles` (when set). */
    isAuthorized: boolean
    /** True until the runtime has finished initialising. */
    isLoading: boolean
}

/**
 * Mobile / non-router navigation guard. Plain hook — the consumer wires
 * the navigation reaction in its own callbacks (e.g. via
 * react-navigation's `useNavigation().reset()`).
 *
 * Callback freshness: consumers can pass inline lambdas safely. The hook
 * tracks the latest callbacks via refs and updates them on every render,
 * so the auth-state effect always invokes the freshest closure without
 * re-firing on every render. This avoids the stale-closure foot-gun while
 * keeping the dependency array minimal.
 *
 * Example:
 *
 * ```ts
 * useRequireAuth({
 *   onUnauthenticated: () =>
 *     navigation.reset({ index: 0, routes: [{ name: Routes.Login }] }),
 * })
 * ```
 */
export const useRequireAuth = (options: UseRequireAuthOptions = {}): UseRequireAuthValue => {
    const { status, isAuthenticated, roles } = useAuth()
    const hasRequiredRole =
        !options.requireRoles ||
        options.requireRoles.length === 0 ||
        options.requireRoles.some(r => roles.includes(r))
    const isLoading = status === 'idle' || status === 'initializing'
    const isAuthorized = isAuthenticated && hasRequiredRole

    // Track the latest callbacks via refs so the auth-state effect below
    // can invoke them without re-firing every time the consumer passes a
    // fresh closure. The refs are updated in an effect (never during
    // render) — pattern borrowed from react-router / react-query.
    const onUnauthenticatedRef = useRef(options.onUnauthenticated)
    const onReauthRequiredRef = useRef(options.onReauthRequired)
    const onForbiddenRef = useRef(options.onForbidden)
    useEffect(() => {
        onUnauthenticatedRef.current = options.onUnauthenticated
        onReauthRequiredRef.current = options.onReauthRequired
        onForbiddenRef.current = options.onForbidden
    })

    useEffect(() => {
        if (isLoading) {
            return
        }
        if (status === 'reauthRequired') {
            onReauthRequiredRef.current?.()
            return
        }
        if (!isAuthenticated) {
            onUnauthenticatedRef.current?.()
            return
        }
        if (!hasRequiredRole) {
            onForbiddenRef.current?.()
        }
    }, [status, isAuthenticated, hasRequiredRole, isLoading])

    return { status, isAuthenticated, isAuthorized, isLoading }
}
